import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UnusedRoomTypesResponse } from 'dashboard/components/visuals-dashboard/widgets/unused-room-types-widget/unused-room-types-data-response.interface';
import { IceDashboardDisplayStateEnum } from 'dashboard/enums/visuals/ice-dashboard-display-state.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { IceVisualsDashboardLargeNumberDisplayComponent } from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-large-number-display/ice-visuals-dashboard-large-number-display.component';
import {
    IceVisualsDashboardTableDisplayComponent,
    TableColumn,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-table-display/ice-visuals-dashboard-table-display.component';

interface ListingData {
    id: string;
    name: string;
    unusedRoomTypesCount: number;
}

@Component({
    selector: 'app-unused-room-types-widget',
    imports: [TranslateModule, IceVisualsDashboardTableDisplayComponent, IceVisualsDashboardLargeNumberDisplayComponent],
    templateUrl: './unused-room-types-widget.component.html',
    styleUrl: './unused-room-types-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class UnusedRoomTypesWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    iceDashboardDisplayStateEnum = IceDashboardDisplayStateEnum;

    onLangChange = toSignal(this.translateService.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    columns: TableColumn[] = [
        { key: 'id', translationKey: 'dashboard.visuals.widgets.unusedRoomTypes.table.columns.id', width: '30%' },
        { key: 'name', translationKey: 'dashboard.visuals.widgets.unusedRoomTypes.table.columns.name', width: '40%' },
        {
            key: 'unusedRoomTypesCount',
            translationKey: 'dashboard.visuals.widgets.unusedRoomTypes.table.columns.unusedRoomTypesCount',
            width: '40%',
        },
    ];

    totalListings = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return 0;
        }
        return widgetData.totalListings;
    });

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    listings = computed<ListingData[]>(() => {
        const widgetData = this.dataService.data();
        this.onLangChange(); // Re-compute when language changes
        if (!widgetData || !widgetData.ranges) {
            return [];
        }
        const listingsArray: ListingData[] = [];
        widgetData.ranges.forEach((range) => {
            const item: ListingData = {
                id: range.mappedId,
                name: range.title,
                unusedRoomTypesCount: range.unusedRoomTypesCount,
            };
            listingsArray.push(item);
        });
        return listingsArray;
    });

    widgetState = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return null;
        }
        return widgetData.totalListings === 0 ? IceDashboardDisplayStateEnum.SUCCESS : IceDashboardDisplayStateEnum.WARNING;
    });

    constructor(
        public dataService: VisualsDashboardDataService<UnusedRoomTypesResponse>,
        private visualsDashboardService: VisualsDashboardService,
        private translateService: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
        effect(() => {
            const state = this.widgetState();
            const widgetHtmlId = this.widget().htmlId;
            if (widgetHtmlId && state !== null) {
                this.visualsDashboardService.setHtmlIdBackgroundStateOverride(widgetHtmlId, state);
            }
        });
    }
}
